<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { Option } from '@ilteducation/exercise-service-types';
import { useLocale } from '@/plugins/translation';
import PlayNarrationButton from '@/components/base/PlayNarrationButton.vue';
import { AudioResource } from '@ilteducation/videos';
import useQuizStore from '@/store/quiz';
import { storeToRefs } from 'pinia';

const props = defineProps<{
  option: Option;
  answerAudio?: AudioResource;
  contentIndex: number;
  correctOption: string;
}>();

const active = ref(false);
const quizStore = useQuizStore();
const { mode, answers, correctAnswers, currentQuestion } = storeToRefs(quizStore);
const { t, l } = useLocale();

const isQuizMode = computed(() => mode.value === 'quiz');

const quizQuestionMode = computed(() => (isQuizMode.value ? 'question-mode' : 'answer-mode'));
const quizQuestionAnswerSelected = computed(() => {
  if (!isQuizMode.value) {
    return '';
  }
  return active.value ? 'active' : '';
});
const pickedOption = computed(() => {
  if (isQuizMode.value) {
    return false;
  }
  const selectedAnswer = correctAnswers.value.find((answer: any) => answer.questionId === props.contentIndex);
  return selectedAnswer.optionId === props.option.id ? 'selected-answer' : false;
});
const correctAnswer = computed(() => {
  if (isQuizMode.value) {
    return false;
  }

  const availableAnswer = correctAnswers.value.find((answer: any) => answer.questionId === props.contentIndex);

  return availableAnswer.correctOptionId === props.option.id ? 'correct-answer' : false;
});
const answerAnnotationText = computed(() => {
  if (pickedOption.value) {
    return t(l.quiz.your_answer);
  }
  return correctAnswer.value ? t(l.quiz.correct_answer) : '';
});

const radioMode = computed(() => {
  if (isQuizMode.value) {
    return active.value ? 'icon-check' : '';
  }
  if (props.correctOption === props.option.id) {
    return 'icon-check';
  }
  return pickedOption.value ? 'icon-close' : '';
});

const selectAnswer = () => {
  if (isQuizMode.value) {
    quizStore.answerQuestion(props.option.id);
  }
};
watch(
  answers,
  (ans) => {
    active.value = !!ans.find(
      (answer: any) => answer.questionId === currentQuestion.value - 1 && answer.optionId === props.option.id,
    );
  },
  { immediate: true, deep: true },
);

const getQuizOptionAriaLabel = (option: Option) => {
  const optionText = option.value;
  let answerText = answerAnnotationText.value;
  if (correctAnswer.value && pickedOption.value) {
    answerText = `${t(l.quiz.your_answer)}. ${t(l.quiz.correct_answer)}`;
  }
  return isQuizMode.value ? optionText : `${optionText}. ${answerText}`;
};
</script>
<template>
  <div
    class="d-flex align-center quiz-question-container"
    :class="[quizQuestionMode, quizQuestionAnswerSelected, pickedOption, correctAnswer]"
    @click="selectAnswer"
    @keydown.enter.prevent.stop="selectAnswer"
    @keydown.space.prevent.stop="selectAnswer"
    role="button"
    tabindex="0"
    :aria-pressed="active"
    :aria-disabled="isQuizMode === false"
    :aria-label="getQuizOptionAriaLabel(option)"
  >
    <div class="quiz-radio-button-container">
      <div role="radio" :aria-checked="active" class="radio-button" aria-hidden="true">
        <div class="checkmark" :class="radioMode"></div>
      </div>
    </div>
    <p :id="`quiz-question-${option.id}`" class="quiz-question">{{ option.value }}</p>
    <span v-if="isQuizMode === false" class="answer-text mr-9">{{ answerAnnotationText }}</span>
    <PlayNarrationButton v-if="answerAudio && answerAudio.id" :value="[answerAudio]" />
  </div>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';

.quiz-questions-container {
  width: 100%;
  margin-top: 1em;
}

@media (max-width: 960px) {
  .quiz-question {
    max-width: 65%;
  }
}

@media (min-width: 960px) and (max-width: 1343px) {
  .quiz-question {
    max-width: 75%;
  }
}

@media (min-width: 1343px) {
  .quiz-question {
    max-width: 85%;
  }
}

.answer-text {
  white-space: nowrap;
}
.play-audio-btn {
  position: absolute;
  right: 10px;
}

.quiz-question-container {
  position: relative;
  min-height: 48px;
  gap: 10px;
  padding: 0.5rem 0.75rem;
  background: rgb(var(--v-theme-surface-container));
  border: 1px solid transparent;
  border-radius: 8px;
  margin-bottom: 16px;

  &:hover {
    .play-audio-btn {
      .v-icon {
        color: rgb(var(--v-theme-green)) !important;
      }
    }
  }

  * {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .quiz-question {
    font-size: 16px;
    color: rgb(var(--v-theme-on-surface));
  }

  .answer-text {
    margin-left: auto;
    font-size: 1rem;
  }

  .quiz-radio-button-container {
    color: rgb(var(--v-theme-on-surface));
    font-weight: 400;
    position: relative;
    width: 35px;

    .radio-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: transparent;
      border-radius: 50%;
      border: 2px solid rgb(var(--v-theme-on-surface-medium));
      cursor: pointer;
      height: 20px;
      width: 20px;
      left: 0;

      .checkmark {
        line-height: 1;
        opacity: 1;
        &:before {
          display: block;
          font-weight: bold;
        }
      }
    }
  }

  &.question-mode {
    cursor: pointer;
    &:hover {
      background: rgb(var(--v-theme-surface));
      box-shadow:
        0 0 4px 0 rgba(18, 43, 64, 0.1),
        0 4px 16px 0 rgba(18, 43, 64, 0.1);
      .radio-button {
        border-color: rgb(var(--v-theme-primary));
      }
    }
  }

  &.active {
    background: rgb(var(--v-theme-surface));
    border-color: rgb(var(--v-theme-primary));
    .radio-button {
      border-color: rgb(var(--v-theme-primary));
      background-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-surface));
    }
  }

  &.answer-mode {
    &.selected-answer {
      background: rgb(var(--v-theme-surface));
      border-color: rgb(var(--v-theme-highlighterred));
      .radio-button {
        border-color: rgb(var(--v-theme-highlighterred));
        background-color: rgb(var(--v-theme-highlighterred));
        color: rgb(var(--v-theme-surface));
      }
      box-shadow: 0 0 0 3px rgb(var(--v-theme-highlighterred-lighten-4));
    }
    &.correct-answer {
      background: rgb(var(--v-theme-surface));
      border-color: rgb(var(--v-theme-green));
      .radio-button {
        border-color: rgb(var(--v-theme-green));
        background-color: rgb(var(--v-theme-green));
        color: rgb(var(--v-theme-surface));
      }
    }
    &.selected-answer.correct-answer {
      box-shadow: 0 0 0 3px rgba(0, 199, 177, 0.3);
    }
  }
}
</style>
